/**
 * @module RecommendationsLoader
 * @description Shared deferred loader for product recommendation sections.
 * Fetches section HTML through Shopify's recommendations endpoint and patches
 * the current custom element in place once recommendations are available.
 * @requires @fc/component
 * @requires @fc/sections
 * @requires @fc/section-response
 * @customElement fc-recommendations
 */
import { FCElement } from '@fc/component';
import { sectionManager } from '@fc/sections';
import { findHTMLNode, isAbortError, LatestRequestController, patchHTML } from '@fc/section-response';

const MARKET_CONTEXT_QUERY_KEYS = ['country', 'locale'];
const RECOMMENDATION_ITEM_SELECTOR = '.resource-list__item, [data-product-id], product-card, product-tile';

class RecommendationsLoader extends FCElement {
  static get observedAttributes() {
    return [
      'data-fetch-state',
      'data-load-immediate',
      'data-preserve-during-refresh',
      'data-recommendations-performed',
      'data-section-id',
      'data-url',
    ];
  }

  #observer = null;
  #loadQueued = false;
  #request = new LatestRequestController();
  #activeRequestUrl = '';

  connectedCallback() {
    super.connectedCallback();
    this.#queueLoad();
  }

  attributeChangedCallback() {
    if (!this.isConnected) return;
    this.#queueLoad();
  }

  disconnectedCallback() {
    this.#disconnectObserver();
    this.#request.abort();
    super.disconnectedCallback();
  }

  onMorphSync() {
    super.onMorphSync();
    this.#queueLoad();
  }

  beforeMorphSync(next) {
    if (!(next instanceof HTMLElement)) return;
    if (!this.#shouldPreserveNextShell(next)) return;

    this.#disconnectObserver();
    this.#request.abort();
    this.#activeRequestUrl = '';

    next.dataset.fetchState = 'ready';
    next.dataset.hasRecommendations = 'true';
    next.dataset.recommendationsPerformed = 'true';
    next.setAttribute('data-skip-subtree-update', 'true');
    this.setAttribute('data-skip-subtree-update', 'true');
    next.setAttribute('aria-busy', 'false');
    next.removeAttribute('inert');
    next.removeAttribute('hidden');
  }

  #queueLoad() {
    if (this.#loadQueued) return;

    this.#loadQueued = true;
    queueMicrotask(() => {
      this.#loadQueued = false;
      this.#startLoad();
    });
  }

  #startLoad() {
    if (this.dataset.recommendationsPerformed === 'true') return;
    if (!this.dataset.url || !this.dataset.sectionId) return;

    const state = this.dataset.fetchState || 'idle';
    const requestUrl = this.#requestUrl();
    if (state !== 'idle') {
      const canSupersedeActiveLoad = state === 'loading' && this.#activeRequestUrl && requestUrl !== this.#activeRequestUrl;
      if (!canSupersedeActiveLoad) return;
    }

    if (this.dataset.loadImmediate === 'true') {
      void this.#load(requestUrl);
      return;
    }

    if (typeof IntersectionObserver === 'undefined') {
      void this.#load(requestUrl);
      return;
    }

    this.#observer ??= new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          this.#disconnectObserver();
          void this.#load();
          break;
        }
      },
      { rootMargin: '400px 0px' },
    );

    this.#observer.observe(this);
  }

  #disconnectObserver() {
    this.#observer?.disconnect();
    this.#observer = null;
  }

  async #load(requestUrl = this.#requestUrl()) {
    const request = this.#request.start();
    this.#activeRequestUrl = requestUrl;
    this.dataset.fetchState = 'loading';
    this.setAttribute('aria-busy', 'true');
    this.#hideWhileLoading();
    this.#disconnectObserver();

    try {
      const doc = await sectionManager.getHTMLDocument(requestUrl, {
        cache: false,
        signal: request.signal,
      });
      if (!request.isCurrent()) return;

      const next = findHTMLNode(doc, `[data-section-id="${this.dataset.sectionId}"]`);
      if (!(next instanceof HTMLElement)) {
        this.#hideUnavailable('empty');
        return;
      }

      const hasRecommendations = next.dataset.hasRecommendations === 'true' && this.#hasRecommendationItems(next);
      const nextState = hasRecommendations ? next.dataset.fetchState || 'ready' : 'empty';
      if (!hasRecommendations) {
        this.#hideUnavailable(nextState);
        return;
      }

      this.#prepareNextForHiddenPatch(next);
      patchHTML(this, doc, next, { childrenOnly: false });
      this.dataset.fetchState = nextState;
      this.dataset.hasRecommendations = 'true';
      this.dataset.recommendationsPerformed = 'true';
      this.setAttribute('aria-busy', 'false');
      this.removeAttribute('hidden');
      this.removeAttribute('inert');
    } catch (error) {
      if (isAbortError(error)) return;
      if (request.isCurrent()) this.#hideUnavailable('failed');
    } finally {
      if (this.#activeRequestUrl === requestUrl) {
        this.#activeRequestUrl = '';
      }
      request.finish();
    }
  }

  #requestUrl() {
    const url = new URL(this.dataset.url, window.location.origin);
    const current = new URL(window.location.href);

    for (const key of MARKET_CONTEXT_QUERY_KEYS) {
      const value = current.searchParams.get(key);
      if (value && !url.searchParams.has(key)) {
        url.searchParams.set(key, value);
      }
    }

    return url.href;
  }

  #hideUnavailable(state) {
    this.dataset.fetchState = state;
    this.dataset.hasRecommendations = 'false';
    this.setAttribute('aria-busy', 'false');
    this.setAttribute('hidden', '');
    this.removeAttribute('inert');
  }

  #hideWhileLoading() {
    if (this.dataset.hideUntilReady !== 'true') return;

    this.setAttribute('hidden', '');
  }

  #prepareNextForHiddenPatch(next) {
    if (this.dataset.hideUntilReady !== 'true') return;

    next.dataset.fetchState = 'loading';
    next.setAttribute('aria-busy', 'true');
    next.setAttribute('hidden', '');
  }

  #shouldPreserveNextShell(next) {
    if (this.dataset.preserveDuringRefresh !== 'true' && next.dataset.preserveDuringRefresh !== 'true') return false;
    if (next.dataset.hideUntilReady !== 'true') return false;
    if (this.hasAttribute('hidden')) return false;
    if (next.dataset.recommendationsPerformed === 'true') return false;
    if (this.dataset.hasRecommendations !== 'true') return false;
    if (!this.#hasRecommendationItems(this)) return false;
    if (this.#hasRecommendationItems(next)) return false;

    const nextState = next.dataset.fetchState || 'idle';
    return nextState === 'idle' || nextState === 'loading';
  }
  #hasRecommendationItems(root) {
    return Boolean(root.querySelector(RECOMMENDATION_ITEM_SELECTOR));
  }
}

if (!customElements.get('fc-recommendations')) {
  customElements.define('fc-recommendations', RecommendationsLoader);
}
